import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../api/api.js';
import CourseForm from '../Components/CourseForm.js';
import CourseList from '../Components/CourseList.js';
import { useToast } from '../Components/ui/Toast.jsx';
import { useConfirm } from '../Components/ui/ConfirmDialog.jsx';
import styles from './CourseManagement.module.css';

const INITIAL_COURSE_FORM_STATE = {
  id: null,
  route_id: '',
  status: 'programmed'
};

const CourseManagement = () => {
  const [courses, setCourses]     = useState([]);
  const [routes, setRoutes]       = useState([]);
  const [courseForm, setCourseForm] = useState(INITIAL_COURSE_FORM_STATE);
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const formRef = useRef(null);

  // Cargar rutas
  useEffect(() => {
    const fetchRoutes = async () => {
      try {
        const routesRes = await api.get('/route');
        setRoutes(routesRes.data || []);
      } catch (err) {

      }
    };
    fetchRoutes();
  }, []);

  // Cargar cursos/viajes
  const fetchCourses = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await api.get('/courses');
      setCourses(res.data || []);
    } catch (err) {

      setCourses([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchCourses(); }, [fetchCourses]);

  const routeOptions = useMemo(() =>
    routes.map(r => ({
      value: String(r.ID),
      label: `${r.Origin} → ${r.Destination} (${r.Duration}h, $${r.Price})`
    }))
  , [routes]);

  // IDs de rutas que ya tienen un viaje activo (no cancelado)
  const activatedRouteIds = useMemo(() =>
    new Set(courses.filter(c => c.Status !== 'cancel').map(c => c.ID_Route))
  , [courses]);

  const resetForm = useCallback(() => setCourseForm(INITIAL_COURSE_FORM_STATE), []);

  const handleEdit = useCallback((course) => {
    setCourseForm({
      id: course.ID,
      route_id: course.ID_Route ? String(course.ID_Route) : '',
      status: course.Status || 'programmed'
    });
    // Scroll automático al formulario
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  }, []);

  const handleDelete = useCallback(async (id) => {
    const ok = await confirm('¿Cancelar este viaje? Esta acción no se puede deshacer.', {
      title: 'Confirmar cancelación',
      confirmText: 'Cancelar viaje',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(`/course/${id}`);
      // Marcar como cancelado en el estado local en vez de eliminarlo
      setCourses(prev => prev.map(c =>
        c.ID === id ? { ...c, Status: 'cancel' } : c
      ));
      if (courseForm.id === id) resetForm();
      showToast('Viaje cancelado correctamente.', { type: 'success' });
    } catch (err) {
      showToast('Error al cancelar el viaje', { type: 'error' });
    }
  }, [courseForm.id, resetForm, confirm, showToast]);

  const handleStatusUpdate = useCallback((id, newStatus) => {
    if (id === null) {
      // Nuevo viaje creado — recargar lista completa
      fetchCourses();
    } else {
      // Edición de estado — actualizar localmente
      setCourses(prev => prev.map(c =>
        c.ID === id ? { ...c, Status: newStatus } : c
      ));
    }
  }, [fetchCourses]);

  return (
    <div className={styles.container}>
      <h2>Gestión de Viajes</h2>

      <div ref={formRef}>
        <CourseForm
          form={courseForm}
          setForm={setCourseForm}
          resetForm={resetForm}
          routeOptions={routeOptions}
          activatedRouteIds={activatedRouteIds}
          onSuccess={handleStatusUpdate}
        />
      </div>

      <h3 className={styles.listTitle}>Lista de Viajes</h3>

      {isLoading && <div className={styles.loadingMessage}>Cargando viajes...</div>}

      {!isLoading && (
        <CourseList
          courses={courses}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};

export default CourseManagement;
